import './index.css'
import { Link } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import {
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CalculatorIcon,
  ClockIcon,
  FlagIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import DocumentMeta from './DocumentMeta.jsx'
import AdUnit from './AdUnit.jsx'
import AppPromo from './AppPromo.jsx'
import { MIN_YEAR, MAX_YEAR, THEMATA_YEAR, SCHEDULE_YEAR } from './constants.js'

const GUIDE_ITEMS = [
  {
    icon: AcademicCapIcon,
    title: 'Τι είναι οι βάσεις εισαγωγής',
    body: 'Βάση είναι ο αριθμός μορίων του τελευταίου υποψηφίου που εισήχθη σε ένα τμήμα. Αλλάζει κάθε χρόνο ανάλογα με τη ζήτηση και τον αριθμό εισακτέων, γι’ αυτό αξίζει να βλέπεις τα ιστορικά δεδομένα πολλών ετών.',
    href: `/vaseis/${MAX_YEAR}`,
    cta: 'Δες βάσεις',
  },
  {
    icon: CalculatorIcon,
    title: 'Πώς υπολογίζονται τα μόρια',
    body: 'Τα μόρια προκύπτουν από τους βαθμούς των εξεταζόμενων μαθημάτων, σταθμισμένους με τους συντελεστές βαρύτητας κάθε επιστημονικού πεδίου, μαζί με τυχόν ειδικά μαθήματα και πρακτικές δοκιμασίες.',
    href: '/ypologismos-morion',
    cta: 'Υπολόγισε μόρια',
  },
  {
    icon: BookOpenIcon,
    title: 'Γιατί βοηθά η εξάσκηση σε παλιά θέματα',
    body: 'Τα θέματα προηγούμενων ετών δείχνουν την τυπολογία των ασκήσεων και τη βαρύτητα κάθε ενότητας. Η εξάσκηση σε συνθήκες εξέτασης σε βοηθά να διαχειρίζεσαι τον χρόνο σου και να εντοπίζεις έγκαιρα τα κενά σου.',
    href: `/themata/${THEMATA_YEAR}`,
    cta: 'Δες θέματα',
  },
]

const FAQ_ITEMS = [
  {
    q: 'Είναι δωρεάν το Πανελλήνιες+;',
    a: 'Ναι. Όλες οι λειτουργίες — βάσεις, θέματα, υπολογισμός μορίων και εργαλεία μελέτης — είναι εντελώς δωρεάν για κάθε μαθητή, χωρίς λογαριασμό ή συνδρομή.',
  },
  {
    q: 'Από πού προέρχονται τα δεδομένα των βάσεων;',
    a: 'Οι βάσεις και τα θέματα βασίζονται σε επίσημα στοιχεία των Πανελληνίων εξετάσεων του Υπουργείου Παιδείας. Ενημερώνουμε τα δεδομένα μετά την ανακοίνωση κάθε νέας εξεταστικής περιόδου.',
  },
  {
    q: 'Για ποια έτη υπάρχουν διαθέσιμες βάσεις και θέματα;',
    a: `Θα βρεις ιστορικά δεδομένα βάσεων από το ${MIN_YEAR} έως το ${MAX_YEAR} και θέματα Πανελληνίων για πολλαπλά έτη, οργανωμένα ανά μάθημα, ώστε να συγκρίνεις την πορεία κάθε τμήματος διαχρονικά.`,
  },
  {
    q: 'Πόσο ακριβής είναι ο υπολογισμός μορίων;',
    a: 'Ο υπολογιστής μορίων χρησιμοποιεί τους συντελεστές βαρύτητας κάθε επιστημονικού πεδίου. Το αποτέλεσμα είναι μια αξιόπιστη εκτίμηση για να συγκρίνεις τα μόριά σου με τις βάσεις των σχολών, αλλά η οριστική μοριοδότηση ανακοινώνεται πάντα από το Υπουργείο.',
  },
  {
    q: 'Πώς μπορώ να οργανώσω το διάβασμά μου;',
    a: 'Μπορείς να χρησιμοποιήσεις το εργαλείο Pomodoro για εστιασμένες συνεδρίες μελέτης και τη λίστα «Στόχοι Σχολών» για να παρακολουθείς τα τμήματα που σε ενδιαφέρουν και τις βάσεις τους.',
  },
]

function Homepage() {
  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works')
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
    <DocumentMeta
      title="Πανελλήνιες+ – Βάσεις, Μόρια & Θέματα"
      description="Βάσεις εισαγωγής, υπολογισμός μορίων, θέματα Πανελληνίων και εργαλεία μελέτης για μαθητές."
      canonical="https://www.panellhniesplus.gr/" /><script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "Πανελλήνιες+",
              url: "https://www.panellhniesplus.gr/",
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `https://www.panellhniesplus.gr/vaseis/${MAX_YEAR}?search={search_term_string}`,
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Πανελλήνιες+",
              url: "https://www.panellhniesplus.gr/",
              logo: "https://www.panellhniesplus.gr/android-chrome-512x512.png",
              description: "Δωρεάν πλατφόρμα για μαθητές που προετοιμάζονται για τις Πανελλήνιες εξετάσεις. Βάσεις, θέματα, υπολογισμός μορίων.",
            },
          ]),
        }} />
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      {/* HERO */}
      <section className="border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs sm:text-sm text-gray-700">
                <span className="h-2 w-2 rounded-full bg-gray-900" />
                Βάσεις • Θέματα • Υπολογισμός μορίων • Pomodoro
              </div>

              <h1 className="mt-4 text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.12]">
                Όλα όσα χρειάζεσαι για τις Πανελλήνιες, σε ένα μέρος.
              </h1>

              <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl">
                Βρες βάσεις ανά τμήμα και έτος, κατέβασε παλιά θέματα για εξάσκηση, υπολόγισε μόρια και οργάνωσε
                διάβασμα με Pomodoro.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center">
                <Link
                  to={`/vaseis/${MAX_YEAR}?ordering=-score`}
                  className="cursor-pointer inline-flex items-center justify-center rounded-xl bg-gray-900 px-5 py-3 text-sm font-medium text-white hover:bg-gray-800 transition"
                >
                  Δες Βάσεις
                </Link>

                <Link
                  to={`/themata/${THEMATA_YEAR}`}
                  className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition"
                >
                  Παλιά Θέματα
                </Link>

                <button
                  onClick={scrollToHowItWorks}
                  className="cursor-pointer inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition"
                >
                  Πώς λειτουργεί
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-500">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4" />
                  Από επίσημες πηγές
                </span>
                <span className="inline-flex items-center gap-2">
                  <ChartBarIcon className="h-4 w-4" />
                  Ιστορικά δεδομένα
                </span>
                <span className="inline-flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  Εύκολο στη χρήση
                </span>
              </div>
            </div>

            {/* Right panel */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Γρήγορη εκκίνηση</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Διάλεξε τι θες να κάνεις τώρα.
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">Panellhnies+</span>
                </div>

                <div className="mt-5 space-y-3">
                  <Link
                    to={`/vaseis/${MAX_YEAR}?ordering=-score`}
                    className="cursor-pointer group flex items-center justify-between rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900 text-white">
                        <AcademicCapIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Βάσεις εισαγωγής</p>
                        <p className="text-xs text-gray-600">Φίλτρα ανά τμήμα/πόλη/έτος</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400 group-hover:text-gray-700">→</span>
                  </Link>

                  <Link
                    to={`/themata/${THEMATA_YEAR}`}
                    className="cursor-pointer group flex items-center justify-between rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                        <BookOpenIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Θέματα</p>
                        <p className="text-xs text-gray-600">Ανά μάθημα & έτος</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400 group-hover:text-gray-700">→</span>
                  </Link>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Link
                      to="/stoxoi-sxolon"
                      className="cursor-pointer group flex items-center gap-3 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                        <FlagIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Στόχοι</p>
                        <p className="text-xs text-gray-600 truncate">Σχολές που στοχεύεις</p>
                      </div>
                      <span className="ml-auto text-sm text-gray-400 group-hover:text-gray-700">→</span>
                    </Link>

                    <Link
                      to={`/programma/${SCHEDULE_YEAR}`}
                      className="cursor-pointer group flex items-center gap-3 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                        <CalendarDaysIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Πρόγραμμα</p>
                        <p className="text-xs text-gray-600 truncate">Ημερομηνίες εξετάσεων</p>
                      </div>
                      <span className="ml-auto text-sm text-gray-400 group-hover:text-gray-700">→</span>
                    </Link>

                    <Link
                      to="/ypologismos-morion"
                      className="cursor-pointer group flex items-center gap-3 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                        <CalculatorIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Μόρια</p>
                        <p className="text-xs text-gray-600 truncate">Υπολογισμός</p>
                      </div>
                      <span className="ml-auto text-sm text-gray-400 group-hover:text-gray-700">→</span>
                    </Link>

                    <Link
                      to="/pomodoro"
                      className="cursor-pointer group flex items-center gap-3 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-900">
                        <ClockIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">Pomodoro</p>
                        <p className="text-xs text-gray-600 truncate">Focus sessions</p>
                      </div>
                      <span className="ml-auto text-sm text-gray-400 group-hover:text-gray-700">→</span>
                    </Link>
                  </div>
                </div>

                <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4">
                  <p className="text-xs text-gray-600">
                    Tip: Δες πρώτα τις <span className="font-medium text-gray-900">Βάσεις</span> για να ξεχωρίσεις 2–3 στόχους.
                    Μετά πήγαινε στα <span className="font-medium text-gray-900">Θέματα</span> για εξάσκηση.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-900">Καθαρή αναζήτηση</p>
              <p className="mt-1 text-sm text-gray-600">
                Φίλτρα και ταξινόμηση χωρίς περιττά βήματα.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-900">Σωστή εξάσκηση</p>
              <p className="mt-1 text-sm text-gray-600">
                Θέματα οργανωμένα ανά μάθημα και έτος.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-medium text-gray-900">Στόχευση σχολών</p>
              <p className="mt-1 text-sm text-gray-600">
                Υπολόγισε μόρια και δες πού “πατάς”.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* APP — σύντομα */}
      <AppPromo />

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-gray-50 border-y border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                Πώς λειτουργεί
              </h2>
              <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-2xl">
                Τρία βήματα, χωρίς περίεργα πράγματα.
              </p>
            </div>

            <Link
              to={`/vaseis/${MAX_YEAR}?ordering=-score`}
              className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 transition w-full md:w-auto"
            >
              Ξεκίνα από Βάσεις →
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900 text-white text-sm font-semibold">
                  1
                </span>
                <p className="text-sm font-medium text-gray-900">Δες Βάσεις</p>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Βρες τμήματα που σε νοιάζουν και δες ιστορικά μόρια ανά έτος.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900 text-white text-sm font-semibold">
                  2
                </span>
                <p className="text-sm font-medium text-gray-900">Κάνε εξάσκηση</p>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Κατέβασε παλιά θέματα και χτίσε ρυθμό με στοχευμένα sessions.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900 text-white text-sm font-semibold">
                  3
                </span>
                <p className="text-sm font-medium text-gray-900">Υπολόγισε μόρια</p>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                Βάλε βαθμούς, δες σύνολο και ξεκαθάρισε στόχους.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Έτοιμος/η να ξεκινήσεις;
              </h3>
              <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-2xl">
                Πήγαινε στις βάσεις, βάλε φίλτρα, και φτιάξε λίστα σχολών σε 2 λεπτά.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Link
                to={`/vaseis/${MAX_YEAR}?ordering=-score`}
                className="cursor-pointer inline-flex items-center justify-center rounded-xl bg-gray-900 px-5 py-3 text-sm font-medium text-white hover:bg-gray-800 transition w-full md:w-auto"
              >
                Πάμε στις Βάσεις
              </Link>
              <Link
                to="/ypologismos-morion"
                className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition w-full md:w-auto"
              >
                Υπολογισμός μορίων
              </Link>
              <Link
                to="/stoxoi-sxolon"
                className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition w-full md:w-auto"
              >
                Στόχοι Σχολών
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GUIDE / INFORMATIONAL CONTENT */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-gray-300" />
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500">
              Οδηγός
            </span>
          </div>
          <h2 className="mt-4 text-2xl sm:text-4xl font-semibold tracking-tight text-gray-900">
            Ο οδηγός σου για τις Πανελλήνιες
          </h2>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed">
            Το Πανελλήνιες+ συγκεντρώνει σε ένα σημείο όσα χρειάζεται ένας υποψήφιος:
            βάσεις εισαγωγής όλων των τμημάτων, θέματα προηγούμενων ετών, υπολογισμό
            μορίων και εργαλεία οργάνωσης του διαβάσματος. Δες πώς λειτουργεί κάθε ενότητα.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {GUIDE_ITEMS.map((item) => (
              <article
                key={item.title}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 transition duration-200 hover:border-gray-300 hover:shadow-sm"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-900 text-white">
                  <item.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-2 flex-1 text-sm text-gray-600 leading-relaxed">{item.body}</p>
                <Link
                  to={item.href}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:text-gray-700"
                >
                  {item.cta}
                  <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-0.5">
                    →
                  </span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* In-content ad — sits between two content-rich sections (Guide / FAQ) */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AdUnit />
      </div>

      {/* FAQ */}
      <section className="bg-gray-50 border-t border-gray-100">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 sm:py-20">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-gray-300" />
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500">
              Συχνές ερωτήσεις
            </span>
          </div>
          <h2 className="mt-4 text-2xl sm:text-4xl font-semibold tracking-tight text-gray-900">
            Απαντήσεις στα πιο συχνά ερωτήματα
          </h2>

          <div className="mt-8 divide-y divide-gray-200 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {FAQ_ITEMS.map((item, i) => (
              <details
                key={item.q}
                open={i === 0}
                className="group px-5 sm:px-6 open:bg-gray-50/40"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left text-base font-medium text-gray-900 [&::-webkit-details-marker]:hidden">
                  {item.q}
                  <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" />
                </summary>
                <p className="pb-5 text-sm sm:text-base text-gray-600 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_ITEMS.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      <Footer />
    </div>
    </>
  )
}

export default Homepage
